import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { inventoryAPI } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  ArrowLeft, MoreVertical, Image as ImageIcon, QrCode, 
  History, Upload, ArrowRightLeft, Bell, ChevronRight,
  Plus, Minus, Camera, Package, Search, RefreshCw,
  X, Check, AlertTriangle, LogOut
} from 'lucide-react';
import BarcodeScanner from '../components/ui/BarcodeScanner';

const MobileInventory = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, workingLocation, setWorkingLocation, loading: authLoading, logout } = useAuth();
  const [locations, setLocations] = useState([]);
  const [showMenu, setShowMenu] = useState(false);
  
  // States
  const [view, setView] = useState('scanner'); // 'scanner', 'search', 'detail'
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showScanner, setShowScanner] = useState(false);
  const [showAdjustment, setShowAdjustment] = useState(false);
  const [adjustmentQty, setAdjustmentQty] = useState('');
  const [adjustmentType, setAdjustmentType] = useState('add');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [transactions, setTransactions] = useState([]);

  // Check if user has inventory manager or clinic admin role
  const hasAccess = user?.role === 'inventory_manager' || user?.role === 'clinic_admin';

  // Auto-select location if not set
  useEffect(() => {
    const fetchAndSetLocation = async () => {
      if (!workingLocation && user) {
        try {
          const { locationsAPI } = await import('../lib/api');
          const response = await locationsAPI.getAll();
          const locs = response.data.items || response.data || [];
          setLocations(locs);
          
          // Auto-select user's assigned location or first location
          if (locs.length > 0) {
            const userLocation = locs.find(l => l.id === user.location_id) || locs[0];
            setWorkingLocation(userLocation);
          }
        } catch (error) {
          console.error('Failed to fetch locations:', error);
        }
      }
    };
    fetchAndSetLocation();
  }, [workingLocation, user, setWorkingLocation]);

  // Check for SKU in URL params
  useEffect(() => {
    const sku = searchParams.get('sku');
    if (sku && hasAccess) {
      searchBySku(sku);
    }
  }, [searchParams, hasAccess]);

  // Show loading state while auth is initializing
  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-teal-600 mx-auto" />
          <p className="mt-2 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect if not authenticated or no access
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (!hasAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  // Search by SKU
  const searchBySku = async (sku) => {
    setLoading(true);
    try {
      const params = { search: sku, page_size: 1 };
      if (workingLocation?.id) params.location_id = workingLocation.id;
      
      const response = await inventoryAPI.getAll(params);
      const items = response.data.items || response.data || [];
      
      if (items.length > 0) {
        setItem(items[0]);
        setView('detail');
      } else {
        toast.error('Item not found');
        setView('scanner');
      }
    } catch (error) {
      console.error('Failed to find item:', error);
      toast.error('Failed to find item');
    } finally {
      setLoading(false);
    }
  };

  // Handle QR scan
  const handleScan = (result) => {
    setShowScanner(false);
    if (result) {
      searchBySku(result);
    }
  };

  // Search items
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    try {
      const params = { search: searchQuery, page_size: 20 };
      if (workingLocation?.id) params.location_id = workingLocation.id;
      
      const response = await inventoryAPI.getAll(params);
      const items = response.data.items || response.data || [];
      setSearchResults(items);
      setView('search');
    } catch (error) {
      console.error('Failed to search:', error);
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  // Select item from search
  const selectItem = (selectedItem) => {
    setItem(selectedItem);
    setView('detail');
    setSearchResults([]);
    setSearchQuery('');
  };

  // Fetch item history
  const fetchHistory = async () => {
    if (!item) return;
    
    try {
      const params = { item_id: item.id, page_size: 20 };
      if (workingLocation?.id) params.location_id = workingLocation.id;
      
      const response = await inventoryAPI.getTransactions(params);
      setTransactions(response.data.items || response.data || []);
      setShowHistory(true);
    } catch (error) {
      console.error('Failed to fetch history:', error);
      toast.error('Failed to load history');
    }
  };

  // Adjust inventory
  const handleAdjustment = async () => {
    if (!adjustmentQty || parseFloat(adjustmentQty) <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    
    if (!adjustmentReason.trim()) {
      toast.error('Please enter a reason');
      return;
    }

    setLoading(true);
    try {
      const quantity = parseInt(adjustmentQty);
      
      const payload = {
        item_id: item.id,
        quantity_change: adjustmentType === 'add' ? quantity : -quantity,
        reason: adjustmentReason
      };
      
      console.log('Adjusting inventory:', payload, 'Type:', adjustmentType);
      
      let adjustResponse;
      if (adjustmentType === 'add') {
        // Use the add endpoint for adding stock (same as clinic admin)
        adjustResponse = await inventoryAPI.add({
          item_id: item.id,
          quantity_change: quantity,
          reason: adjustmentReason
        });
      } else {
        // Use adjust endpoint for removing stock
        adjustResponse = await inventoryAPI.adjust(payload);
      }
      
      console.log('Adjust response:', adjustResponse.data);
      
      // Use the response from adjust which already has the updated quantity
      if (adjustResponse.data) {
        setItem(adjustResponse.data);
        toast.success(`Inventory ${adjustmentType === 'add' ? 'added' : 'removed'} successfully. New stock: ${adjustResponse.data.quantity_on_hand}`);
      } else {
        // Fallback: Refresh item data
        const response = await inventoryAPI.getById(item.id);
        setItem(response.data);
        toast.success(`Inventory ${adjustmentType === 'add' ? 'added' : 'removed'} successfully`);
      }
      
      setShowAdjustment(false);
      setAdjustmentQty('');
      setAdjustmentReason('');
    } catch (error) {
      console.error('Failed to adjust inventory:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to adjust inventory');
    } finally {
      setLoading(false);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Scanner View
  const renderScannerView = () => (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="w-10" />
        <h1 className="text-lg font-semibold text-gray-900">Inventory Scanner</h1>
        <div className="relative">
          <button 
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 -mr-2 hover:bg-gray-100 rounded-lg"
          >
            <MoreVertical className="w-5 h-5 text-gray-600" />
          </button>
          
          {/* Dropdown Menu */}
          {showMenu && (
            <>
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setShowMenu(false)} 
              />
              <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-lg border border-gray-100 z-50 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900 truncate">{user?.name || user?.email}</p>
                  <p className="text-xs text-gray-500">{user?.role?.replace('_', ' ')}</p>
                </div>
                <button
                  onClick={() => {
                    setShowMenu(false);
                    logout();
                    navigate('/login');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">Sign Out</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8">
        {/* QR Scanner Button */}
        <button
          onClick={() => setShowScanner(true)}
          className="w-40 h-40 rounded-3xl bg-gradient-to-br from-teal-500 to-teal-600 flex flex-col items-center justify-center shadow-lg active:scale-95 transition-transform"
        >
          <QrCode className="w-16 h-16 text-white mb-2" />
          <span className="text-white font-medium">Scan QR Code</span>
        </button>

        <p className="text-gray-500 text-sm mt-6 text-center">
          Scan item QR code to view details<br/>and adjust inventory
        </p>

        {/* Divider */}
        <div className="flex items-center gap-4 my-8 w-full">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-gray-400 text-sm">or</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        {/* Search Input */}
        <div className="w-full max-w-sm">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              placeholder="Search by name or SKU..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="pl-10 pr-4 h-12 text-base rounded-xl border-gray-200"
            />
          </div>
          <Button
            onClick={handleSearch}
            disabled={loading || !searchQuery.trim()}
            className="w-full mt-3 h-12 rounded-xl bg-gray-900 hover:bg-gray-800 text-white font-medium"
          >
            {loading ? 'Searching...' : 'Search Items'}
          </Button>
        </div>
      </div>

      {/* Location indicator */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
        <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
          <Package className="w-4 h-4" />
          <span>{workingLocation?.name || 'No location selected'}</span>
        </div>
      </div>

      {/* QR Scanner Modal */}
      {showScanner && (
        <BarcodeScanner
          isOpen={showScanner}
          onClose={() => setShowScanner(false)}
          onScan={handleScan}
        />
      )}
    </div>
  );

  // Search Results View
  const renderSearchView = () => (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
        <button onClick={() => setView('scanner')} className="p-2 -ml-2">
          <ArrowLeft className="w-6 h-6 text-gray-700" />
        </button>
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="pl-10 h-10 rounded-lg border-gray-200"
          />
        </div>
        <button onClick={handleSearch} className="p-2">
          <RefreshCw className={`w-5 h-5 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-y-auto">
        {searchResults.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <Package className="w-12 h-12 mb-3" />
            <p>No items found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {searchResults.map((result) => (
              <button
                key={result.id}
                onClick={() => selectItem(result)}
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 active:bg-gray-100 text-left"
              >
                <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                  {result.image_url ? (
                    <img src={result.image_url} alt="" className="w-full h-full object-cover rounded-lg" />
                  ) : (
                    <Package className="w-6 h-6 text-gray-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{result.name}</p>
                  <p className="text-sm text-gray-500">SKU: {result.sku}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">{result.quantity_on_hand}</p>
                  <p className="text-xs text-gray-400">in stock</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-300" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Item Detail View
  const renderDetailView = () => {
    if (!item) return null;
    
    const isLowStock = item.quantity_on_hand <= (item.reorder_level || item.reorder_threshold || 10);
    const totalValue = (item.quantity_on_hand || 0) * (item.selling_price || item.unit_price || 0);
    
    return (
      <div className="flex flex-col h-full bg-gray-50">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-100">
          <button onClick={() => setView('scanner')} className="p-2 -ml-2">
            <ArrowLeft className="w-6 h-6 text-gray-700" />
          </button>
          <button className="p-2 -mr-2">
            <MoreVertical className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Image Section */}
          <div className="bg-white px-4 py-8">
            <div className="flex flex-col items-center">
              {item.image_url ? (
                <img 
                  src={item.image_url} 
                  alt={item.name} 
                  className="w-40 h-40 object-contain rounded-xl bg-gray-50"
                />
              ) : (
                <div className="w-40 h-40 rounded-xl bg-gray-100 flex flex-col items-center justify-center">
                  <ImageIcon className="w-12 h-12 text-gray-300 mb-2" />
                  <p className="text-sm text-gray-400 text-center px-4">
                    No image available
                  </p>
                </div>
              )}
              
              {/* Toggle buttons */}
              <div className="flex items-center gap-2 mt-4">
                <button className="px-4 py-2 rounded-lg border border-gray-200 bg-white flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-600">Image</span>
                </button>
                <button className="px-4 py-2 rounded-lg border border-gray-200 bg-white flex items-center gap-2">
                  <QrCode className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-600">QR</span>
                </button>
              </div>
            </div>
          </div>

          {/* Item Info */}
          <div className="bg-white mt-2 px-4 py-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <Package className="w-4 h-4" />
              <span>{workingLocation?.name || 'Unknown Location'}</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">{item.name}</h1>
            
            <div className="flex gap-8">
              <div>
                <p className="text-sm text-gray-400">SKU</p>
                <p className="font-medium text-gray-900">{item.sku}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Updated at</p>
                <p className="font-medium text-gray-900">{formatDate(item.updated_at)}</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-white mt-2 px-4 py-4">
            <div className="flex justify-around">
              <button 
                onClick={() => setShowAdjustment(true)}
                className="flex flex-col items-center gap-2"
              >
                <div className="w-14 h-14 rounded-full border-2 border-gray-200 flex items-center justify-center">
                  <ArrowRightLeft className="w-6 h-6 text-gray-600" />
                </div>
                <span className="text-sm text-gray-600">Adjust</span>
              </button>
              <button 
                onClick={fetchHistory}
                className="flex flex-col items-center gap-2"
              >
                <div className="w-14 h-14 rounded-full border-2 border-gray-200 flex items-center justify-center">
                  <History className="w-6 h-6 text-gray-600" />
                </div>
                <span className="text-sm text-gray-600">History</span>
              </button>
              <button className="flex flex-col items-center gap-2">
                <div className="w-14 h-14 rounded-full border-2 border-gray-200 flex items-center justify-center">
                  <Upload className="w-6 h-6 text-gray-600" />
                </div>
                <span className="text-sm text-gray-600">Export</span>
              </button>
            </div>
          </div>

          {/* Metrics */}
          <div className="bg-white mt-2 px-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Quantity */}
              <div className="flex items-center justify-between border-r border-gray-100 pr-4">
                <div>
                  <p className="text-sm text-gray-400">Quantity · {item.unit_of_measure || 'unit'}</p>
                  <p className="text-2xl font-bold text-gray-900">{item.quantity_on_hand || 0}</p>
                </div>
                <button 
                  onClick={() => setShowAdjustment(true)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <ArrowRightLeft className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              
              {/* Min Level */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Min Level</p>
                  <p className="text-2xl font-bold text-gray-900">{item.reorder_level || item.reorder_threshold || 10}</p>
                </div>
                {isLowStock && (
                  <Bell className="w-5 h-5 text-red-500" />
                )}
              </div>
              
              {/* Price */}
              <div className="border-r border-gray-100 pr-4 pt-4">
                <p className="text-sm text-gray-400">Price</p>
                <p className="text-2xl font-bold text-gray-900">${(item.selling_price || item.unit_price || 0).toFixed(2)}</p>
              </div>
              
              {/* Total Value */}
              <div className="pt-4">
                <p className="text-sm text-gray-400">Total Value</p>
                <p className="text-2xl font-bold text-gray-900">${totalValue.toFixed(2)}</p>
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="bg-white mt-2">
            <button className="w-full px-4 py-4 flex items-center justify-between border-b border-gray-100">
              <span className="text-gray-400">Category</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-900">{item.category || 'Medication'}</span>
                <ChevronRight className="w-5 h-5 text-gray-300" />
              </div>
            </button>
            <button className="w-full px-4 py-4 flex items-center justify-between border-b border-gray-100">
              <span className="text-gray-400">Manufacturer</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-900">{item.manufacturer || '-'}</span>
                <ChevronRight className="w-5 h-5 text-gray-300" />
              </div>
            </button>
            <button className="w-full px-4 py-4 flex items-center justify-between">
              <span className="text-gray-400">Notes</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-500 text-sm">{item.description || 'No notes'}</span>
                <ChevronRight className="w-5 h-5 text-gray-300" />
              </div>
            </button>
          </div>

          {/* Low Stock Warning */}
          {isLowStock && (
            <div className="mx-4 my-4 p-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <div>
                <p className="font-medium text-red-700">Low Stock Alert</p>
                <p className="text-sm text-red-600">
                  Current stock ({item.quantity_on_hand}) is at or below minimum level ({item.reorder_level || item.reorder_threshold || 10})
                </p>
              </div>
            </div>
          )}

          {/* Bottom spacing */}
          <div className="h-6" />
        </div>

        {/* Adjustment Modal */}
        {showAdjustment && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
            <div className="w-full bg-white rounded-t-3xl p-6 animate-slide-up">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Adjust Inventory</h2>
                <button onClick={() => setShowAdjustment(false)} className="p-2 -mr-2">
                  <X className="w-6 h-6 text-gray-500" />
                </button>
              </div>

              {/* Type Toggle */}
              <div className="flex gap-2 mb-6">
                <button
                  onClick={() => setAdjustmentType('add')}
                  className={`flex-1 py-3 rounded-xl font-medium flex items-center justify-center gap-2 ${
                    adjustmentType === 'add' 
                      ? 'bg-green-500 text-white' 
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  <Plus className="w-5 h-5" />
                  Add Stock
                </button>
                <button
                  onClick={() => setAdjustmentType('remove')}
                  className={`flex-1 py-3 rounded-xl font-medium flex items-center justify-center gap-2 ${
                    adjustmentType === 'remove' 
                      ? 'bg-red-500 text-white' 
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  <Minus className="w-5 h-5" />
                  Remove Stock
                </button>
              </div>

              {/* Quantity Input */}
              <div className="mb-4">
                <label className="block text-sm text-gray-500 mb-2">Quantity</label>
                <Input
                  type="number"
                  placeholder="Enter quantity"
                  value={adjustmentQty}
                  onChange={(e) => setAdjustmentQty(e.target.value)}
                  className="h-12 text-lg rounded-xl"
                />
              </div>

              {/* Reason Input */}
              <div className="mb-6">
                <label className="block text-sm text-gray-500 mb-2">Reason</label>
                <Input
                  placeholder="Enter reason for adjustment"
                  value={adjustmentReason}
                  onChange={(e) => setAdjustmentReason(e.target.value)}
                  className="h-12 rounded-xl"
                />
              </div>

              {/* Current Stock Info */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-500">Current Stock</span>
                  <span className="font-medium text-gray-900">{item.quantity_on_hand}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-500">Adjustment</span>
                  <span className={`font-medium ${adjustmentType === 'add' ? 'text-green-600' : 'text-red-600'}`}>
                    {adjustmentType === 'add' ? '+' : '-'}{adjustmentQty || 0}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-200">
                  <span className="text-gray-700 font-medium">New Stock</span>
                  <span className="font-bold text-gray-900">
                    {adjustmentType === 'add' 
                      ? (item.quantity_on_hand || 0) + (parseFloat(adjustmentQty) || 0)
                      : Math.max(0, (item.quantity_on_hand || 0) - (parseFloat(adjustmentQty) || 0))
                    }
                  </span>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                onClick={handleAdjustment}
                disabled={loading || !adjustmentQty || !adjustmentReason.trim()}
                className={`w-full h-14 rounded-xl font-medium text-lg ${
                  adjustmentType === 'add' 
                    ? 'bg-green-500 hover:bg-green-600' 
                    : 'bg-red-500 hover:bg-red-600'
                } text-white`}
              >
                {loading ? 'Processing...' : `Confirm ${adjustmentType === 'add' ? 'Addition' : 'Removal'}`}
              </Button>
            </div>
          </div>
        )}

        {/* History Modal */}
        {showHistory && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
            <div className="w-full h-[80vh] bg-white rounded-t-3xl flex flex-col">
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <h2 className="text-xl font-bold text-gray-900">Transaction History</h2>
                <button onClick={() => setShowHistory(false)} className="p-2 -mr-2">
                  <X className="w-6 h-6 text-gray-500" />
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto">
                {transactions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                    <History className="w-12 h-12 mb-3" />
                    <p>No transaction history</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {transactions.map((txn, idx) => (
                      <div key={idx} className="px-4 py-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-sm font-medium px-2 py-0.5 rounded ${
                            txn.action_type === 'add' || txn.action_type === 'received'
                              ? 'bg-green-100 text-green-700'
                              : txn.action_type === 'consumed' || txn.action_type === 'remove'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {txn.action_type?.toUpperCase()}
                          </span>
                          <span className={`font-bold ${
                            txn.quantity > 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {txn.quantity > 0 ? '+' : ''}{txn.quantity}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">{txn.reason || 'No reason provided'}</p>
                        <p className="text-xs text-gray-400">
                          {formatDate(txn.timestamp)} • {txn.performed_by_name || 'System'}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Main render
  return (
    <div className="h-screen bg-white">
      {view === 'scanner' && renderScannerView()}
      {view === 'search' && renderSearchView()}
      {view === 'detail' && renderDetailView()}
    </div>
  );
};

export default MobileInventory;
